import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit, output } from '@angular/core';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { AvatarModule } from 'primeng/avatar';
import { AvatarGroupModule } from 'primeng/avatargroup';
import { ButtonModule } from 'primeng/button';
import { CardModule } from 'primeng/card';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { DialogService, DynamicDialogModule, DynamicDialogRef } from 'primeng/dynamicdialog';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputTextModule } from 'primeng/inputtext';
import { MessageModule } from 'primeng/message';
import { MessagesModule } from 'primeng/messages';
import { ToastModule } from 'primeng/toast';
import { ToggleButtonModule } from 'primeng/togglebutton';
import { GenericErrorResponse } from '../../../../../core/models/generic-response.model';
import { HttpErrorResponse } from '@angular/common/http';
import { InlineResponse } from '../../../../../core/models/inline-response.model';
import { ReferentialsCedService } from '../referentials-ced.service';
import { Subject } from 'rxjs';
import { CheckboxModule } from 'primeng/checkbox';
import { NiveauCED } from '../referentials-ced.component';
import { DropdownModule } from 'primeng/dropdown';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { provideTranslocoScope, TranslocoModule, TranslocoService } from '@jsverse/transloco';

enum niveauCED {
  STRING,
}

@Component({
  selector: 'app-add-ced',
  standalone: true,
  imports: [CommonModule, DialogModule, ButtonModule, CheckboxModule, DropdownModule, DynamicDialogModule, InputTextModule, InputGroupModule, InputGroupAddonModule, ConfirmDialogModule, ReactiveFormsModule, ToastModule, MessagesModule, MessageModule, ProgressSpinnerModule, TranslocoModule],
  providers: [DialogService, MessageService, provideTranslocoScope({ scope: 'referentiels' })],
  templateUrl: './add-ced.component.html',
  styleUrl: './add-ced.component.css',
})
export class AddCedComponent implements OnInit, OnDestroy {
  private destroy$: Subject<boolean> = new Subject<boolean>();

  disableSubmit = false;
  errors: GenericErrorResponse[] = [];
  submitting: boolean = false;

  cedForm!: FormGroup;

  danger: boolean = false;
  selectedDanger: boolean | undefined;
  niveauCedOptions: { label: string; value: string }[] = [];

  constructor(
    private formBuilder: FormBuilder,
    private ref: DynamicDialogRef,
    private cedService: ReferentialsCedService,
    private messageService: MessageService,
    private translocoService: TranslocoService
  ) {}

  ngOnInit(): void {
    this.cedForm = this.formBuilder.group({
      code: ['', [Validators.required, Validators.maxLength(6), Validators.pattern('^[0-9]*$')]],
      danger: [false, [Validators.required]],
      libelle: ['', [Validators.required, Validators.maxLength(250)]],
      niveauCED: ['', [Validators.required]],
    });

    this.niveauCedOptions = this.getNiveauCedOptions();
  }

  getNiveauCedOptions(): { label: string; value: string }[] {
    const allowedValues = ['CATEGORIE', 'SOUS_CATEGORIE', 'DECHET']; // Les valeurs désirées
    return Object.keys(NiveauCED)
      .filter((key) => allowedValues.includes(key)) // Ne garder que les options spécifiques
      .map((key) => {
        return { label: key, value: key };
      });
  }

  addCed() {
    this.submitting = true;
    const formValue = this.cedForm.value;

    this.cedService.addCedData(formValue.code, formValue.danger, formValue.libelle, formValue.niveauCED).subscribe({
      next: (response) => {
        this.handleInlineResponse(response);
        this.submitting = false;
      },
      error: (error) => {
        this.handleHttpErrorResponse(error);
      },
    });
  }

  private handleInlineResponse(response: InlineResponse): void {
    this.disableSubmit = false;
    if (response.success) {
      this.close(true);
    } else {
      this.errors = response.errors;
      this.messageService.add({
        severity: 'error',
        summary: this.translocoService.translate('error.popup.titre.defaut'),
        detail: 'Une erreur est survenue : ' + this.errors.map((e) => e.errorMsg).join(', '),
        life: 3000,
      });
    }
  }

  private handleHttpErrorResponse(error: HttpErrorResponse) {
    this.disableSubmit = false;
    this.errors = [];
    this.errors.push({
      errorInputId: error.status.toString(),
      errorCode: error.status.toString(),
      errorMsg: error.statusText,
    } as GenericErrorResponse);
    this.messageService.add({ severity: 'error', summary: this.translocoService.translate('error.popup.titre.defaut'), detail: 'Une erreur est survenue : ' + error.statusText, life: 3000 });
  }

  close(result: boolean): void {
    this.ref.close(result);
  }

  ngOnDestroy(): void {
    this.destroy$.next(true);
    this.destroy$.unsubscribe();
  }
}
